import type es from '../es/plan';

/** `plan` in English. Same keys as `es/plan.ts`, enforced by the type. */
const plan: typeof es = {
  labels: {
    cadence: 'a month',
    monthly: (price: string) => `TBit Jobs Plan (${price} a month)`,
    quarterly: 'TBit Jobs quarterly plan',
    legacy: (code: string) => `${code} plan`,
    none: 'No active plan',
  },
  yourPlan: 'Your plan',
  offer: {
    benefits: {
      send: 'We apply for you every business day, without you opening your computer.',
      mail: 'Employer replies land in the inbox you already use.',
      doc: 'We tailor your resume and your message to each job.',
    },
    partner: (code: string, amount: string, months: number | 'forever') =>
      months === 'forever'
        ? `With code ${code}: ${amount} off every month.`
        : months === 1
          ? `With code ${code}: ${amount} off your first month.`
          : `With code ${code}: ${amount} off each of your first ${months} months.`,
    firstMonths: (months: number) => (months === 1 ? 'your first month' : `a month for your first ${months} months`),
    after: (price: string) => `Then ${price} a month.`,
    listPrice: (price: string) => `Regular price ${price}`,
    secureNote: 'Secure payment with Stripe. Renews every month.',
    /** The REQUIRED checkbox for the TBit Jobs terms (price, trial, cancellation, refunds), above the button. */
    terms: {
      before: 'I accept the ',
      label: 'TBit Jobs Terms and Conditions',
      href: 'https://tbit.app/legal/tbit-jobs-terms-and-conditions',
      after: '.',
      required: 'Check the box to accept the terms and continue.',
      outdated: 'We updated the terms. Read them and check the box again.',
    },
    trial: {
      badge: 'First 7 days free',
      card: "We ask for your card to get started, but we don't charge you anything for 7 days.",
      charge: (price: string) => `Billing starts on day 8: ${price} a month from then on.`,
      chargeIntro: (first: string, months: number, price: string) =>
        months === 1
          ? `Billing starts on day 8: ${first} for that first month, then ${price} a month.`
          : `Billing starts on day 8: ${first} a month for your first ${months} months, then ${price} a month.`,
      cancel: "If you cancel before the 7 days are up, you don't pay anything.",
      secureNote: 'Secure payment with Stripe.',
    },
    errors: {
      alreadySubscribed: 'Your subscription is still active. Reload the page.',
      checkoutUnavailable: "We couldn't open checkout right now. Write to us and we'll activate your plan with you.",
      checkoutFailed: "We couldn't open checkout. Try again in a moment.",
      network: 'Check your connection and try again.',
    },
  },
  coupon: {
    open: 'Have a discount code?',
    label: 'Discount code',
    placeholder: 'YOUR CODE',
    apply: 'Apply',
    remove: 'Remove',
    partnerHint: 'If it is a partner code, we share your email and your plan status with the person who invited you.',
    expired: "That code doesn't exist or has expired.",
    unavailable: "We couldn't check the code. You can try it on the payment page.",
    applied: (code: string) => `Code ${code} applied`,
    once: 'off your first paid month',
    firstMonth: 'off your first month',
    forever: 'off every month',
    months: (n: number) => `for ${n} ${n === 1 ? 'month' : 'months'}`,
  },
  nudge: {
    trial: {
      ribbon: "Your free trial isn't active: we're not applying for you yet.",
      headline: "Your free trial isn't active",
      lede: "Until you start it, your applications don't go out to any employer. It's 7 days free and you can cancel anytime.",
    },
    reactivate: {
      ribbon: "Your plan expired: we're not applying for you.",
      headline: "Your plan isn't active",
      lede: "Your applications don't go out to employers until you reactivate it. We pick up where you left off.",
    },
    activate: {
      ribbon: "You don't have an active plan: we're not applying for you.",
      headline: "You don't have an active plan",
      lede: "Your applications don't go out to employers until you activate your plan.",
    },
  },
  cta: {
    trial: {
      label: 'Start your 7 free days',
      note: "We ask for your card, but we don't charge you anything for 7 days.",
    },
    reactivate: {
      label: 'Reactivate my plan',
      note: (price: string) => `${price} a month. We pick up where you left off.`,
    },
    activate: {
      label: 'Activate my plan',
      note: (price: string) => `${price} a month. Cancel anytime.`,
    },
  },
  paywall: {
    label: 'Activate your plan',
    headline: "You've seen how your applications arrive",
    lede: "Activate your plan and we'll send them to real employers, every business day.",
    early: {
      headline: "Whenever you're ready, we apply for you",
      lede: 'Your first 7 days are free. Send yourself the samples above to see how they arrive, or start now: they go out to employers every business day.',
      ledeNoTrial: 'Send yourself the samples above to see how they arrive, or activate your plan now: they go out to employers every business day.',
    },
    notNow: 'Not now',
    close: 'Close',
  },
  paused: {
    title: 'Your payment did not go through',
    body: 'We paused your applications until you update your payment method. As soon as you change it, we charge your plan and start applying for you again.',
    cta: 'Update payment method',
  },
  page: {
    loading: 'Loading your plan',
    loadError: "We couldn't load your plan. Reload the page.",
    active: 'Active',
    expired: 'Expired',
    renewsBefore: 'Renews on ',
    renewsAfter: (price: string) => ` for ${price}.`,
    renewsPlain: '.',
    trialing: 'Free trial',
    trialBefore: 'Free trial until ',
    trialAfter: (price: string) =>
      `. When it ends, you're charged ${price} a month. Cancel before then and you don't pay anything.`,
    activeBody:
      "While it's active, we apply for you every business day, tailor your resume to each job, and bring the replies to your inbox.",
    changeOrCancel: "Need to change or cancel your plan? Write to us and we'll handle it with you.",
    manage: {
      cta: 'Manage or cancel my plan',
      opening: 'Opening...',
      note: 'Update your card, see your invoices, or cancel anytime.',
      noBillingAccount: "Your plan can't be managed from here. Write to us and we'll change or cancel it with you.",
      failed: "We couldn't open your plan settings. Try again in a moment.",
      failedCharge: 'Did a payment fail? Update your card here.',
    },
    endedOn: (date: string) => ` expired on ${date}.`,
    notCurrent: ' is no longer active.',
    meanwhile: ' Until you reactivate it, no applications go out to employers.',
    reactivate: {
      headline: 'Reactivate your plan',
      lede: "We'll go back to applying for you every business day, right where you left off.",
    },
    discover: {
      headline: 'We apply for you, every day',
      lede: "You've already seen how your application arrives. With your plan active, those same applications go out to employers while you do something else.",
    },
    compare: {
      headline: 'You apply. With the plan, we apply for you.',
      have: {
        title: 'TBit Jobs',
        chip: 'You already have it',
        items: [
          'The jobs that fit your profile',
          'Apply on your own with a letter in English',
          'Your résumé score and what it is missing',
          'Your public profile and the talent pool',
        ],
        footer: 'Always, no card needed.',
      },
      planTitle: 'TBit Jobs plan',
      ribbon: 'We do it for you',
      photoAlt: 'Hotel housekeeper next to her towel cart',
      caption: 'While you work, TBit applies.',
      points: [
        'We apply for you every business day.',
        'A letter for each employer, written from your résumé.',
        'We read the replies, tell you which ones are an opportunity and what to answer.',
        'We rewrite your résumé and translate it for each country.',
      ],
      never:
        'The plan does not buy a job, priority with an employer or jobs you would not see for free. Every job is in the search, with or without the plan: the plan picks them for you and applies. You show up the same in searches.',
    },
  },
};

export default plan;
